<!-- Responsibility: Implement sys gallery modal behavior for the ui/panels surface.
Scope: Owns ui/panels concerns only; cross-surface orchestration is excluded. -->
<sys-gallery-modal>
  <dialog ref="galleryDialog" class="gallery-modal" onclick={ onGalleryBackdrop } oncancel={ onGalleryCancel }>
    <div class="gallery-content">
      <div class="gallery-header">
        <h2>VISION GALLERY</h2>
      </div>
      <div class="gallery-scroll modal-scroll-body">
        <p if={ state.galleryLoading } class="gallery-empty">Scanning IndexedDB…</p>
        <p if={ !state.galleryLoading && !state.galleryImages.length } class="gallery-empty">
          No cached images yet. Play the game to generate visions and portraits.
        </p>
        <div class="gallery-grid" if={ state.galleryImages.length }>
          <div each={ img in state.galleryImages } class="gallery-tile">
            <img src={ img.url } class="gallery-img" alt={ img.label }>
            <span class="gallery-label">{ img.label }</span>
          </div>
        </div>
      </div>
    </div>
  </dialog>

  <style>
    .gallery-modal {
      background: var(--surface, #0a0e1a);
      border: 1px solid var(--border, #1e2a3a);
      border-radius: 0.375rem;
      padding: 0;
      width: min(92vw, 42.5rem);
      max-height: 85vh;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      color: var(--text);
      &::backdrop { background: rgba(0,0,0,0.7); }
    }

    .gallery-content {
      display: flex;
      flex-direction: column;
      overflow: hidden;
      height: 100%;
    }

    .gallery-scroll {
      min-height: 0;
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .gallery-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--space-sm) var(--space-md);
      border-bottom: 1px solid var(--border);
      flex-shrink: 0;
      h2 { font-size: 0.85rem; letter-spacing: 0.12em; margin: 0; color: var(--gold); }
    }

    .gallery-empty {
      font-size: var(--space-sm);
      color: var(--text-dim);
      padding: var(--space-lg);
      text-align: center;
      margin: 0;
    }

    .gallery-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(8.75rem, 1fr));
      gap: var(--space-xs);
      padding: var(--space-sm);
    }

    .gallery-tile {
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
    }

    .gallery-img {
      width: 100%;
      aspect-ratio: 1;
      object-fit: cover;
      border-radius: 0.1875rem;
      border: 1px solid var(--border);
      display: block;
    }

    .gallery-label {
      font-size: var(--type-3xs);
      color: var(--text-dim);
      letter-spacing: 0.06em;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  </style>

  <script>
    import localforage from 'localforage'
    import { playEffect } from '../../engine/audio.js'
    import { dispatcher } from '../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"

    function labelFor(key) {
      if (key.startsWith('vision_')) return 'VISION · ' + key.slice(7)
      if (key.startsWith('portrait_')) return 'PORTRAIT · ' + key.slice(8)
      if (key.startsWith('npc_img_')) return 'PERSON · ' + key.slice(8).replace(/_[0-9a-f]+$/, '').replace(/_/g, ' ')
      if (key.startsWith('item_img_')) return 'ITEM · ' + key.slice(9).replace(/_/g, ' ')
      return key
    }

    export default {
      state: {
        galleryImages: [],
        galleryLoading: false,
      },

      onMounted() {
        this.lastOpenSeq = this.props.openSeq ?? 0
        this.closeHandle = dispatcher.on(TOPICS.ui.sysGalleryCloseRequested, () => {
          this.closeGallery()
        })
      },

      onUnmounted() {
        if (this.closeHandle) dispatcher.off(this.closeHandle)
      },

      onUpdated() {
        const seq = this.props.openSeq ?? 0
        if (seq === this.lastOpenSeq) return
        this.lastOpenSeq = seq
        this.openGallery()
      },

      async openGallery() {
        playEffect('ui_open')
        this.update({ galleryLoading: true, galleryImages: [] })
        this.$('[ref="galleryDialog"]')?.showModal()
        dispatcher.trigger('sys-gallery-modal', TOPICS.ui.overlayOpened, { key: 'sys-gallery-modal' })
        try {
          const keys = await localforage.keys()
          const imgKeys = keys.filter(k =>
            k.startsWith('vision_') || k.startsWith('portrait_') ||
            k.startsWith('npc_img_') || k.startsWith('item_img_')
          )
          const entries = await Promise.all(imgKeys.map(async k => {
            const url = await localforage.getItem(k)
            return url ? { key: k, url, label: labelFor(k) } : null
          }))
          this.update({ galleryImages: entries.filter(Boolean), galleryLoading: false })
        } catch {
          this.update({ galleryLoading: false })
        }
      },

      closeGallery() {
        const dialog = this.$('[ref="galleryDialog"]')
        if (!dialog?.open) return
        playEffect('ui_close')
        dialog.close()
        dispatcher.trigger('sys-gallery-modal', TOPICS.ui.overlayClosed, { key: 'sys-gallery-modal' })
      },

      onGalleryBackdrop(e) {
        if (e.target === this.$('[ref="galleryDialog"]')) this.closeGallery()
      },

      onGalleryCancel(e) {
        e.preventDefault()
        this.closeGallery()
      },
    }
  </script>
</sys-gallery-modal>
